// apps/web/src/components/shell/shell-topbar.tsx — Fase 06, Plano 05 (D-04, D-16); 17.8 Plano 09 (D-16, U-10).
// Topbar de 52px: botão da sidebar, breadcrumb compacto, slot das abas, indicador ao vivo (Plano 12) e alternância de tema.
// O avatar, o nome, o perfil e o Sair moram no menu do avatar, no rodapé da sidebar.
import { Fragment, useEffect, useState, type ReactElement } from 'react';
import { MoreHorizontal, PanelLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useSidebar } from '../ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';
import { resolverBreadcrumbs, type BreadcrumbSegment } from './navegacao';
import { AppearanceToggle } from './appearance';
import { IndicadorAoVivo } from './indicador-ao-vivo';
import { AlvoSlotTopbar } from './topbar-slot';
import { AjudaContextual } from '../ajuda/ajuda-contextual';

export interface ShellTopbarProps {
  readonly pathname: string;
}

const CONSULTA_ESTREITA = '(max-width: 1023px)';

/** Abaixo de 1024px o breadcrumb encolhe: ficam o primeiro e o atual, o resto vira "…". */
function useBreadcrumbCompacto(): boolean {
  const [compacto, setCompacto] = useState<boolean>(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(CONSULTA_ESTREITA).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const consulta = window.matchMedia(CONSULTA_ESTREITA);
    const atualizar = (): void => setCompacto(consulta.matches);
    atualizar();
    consulta.addEventListener('change', atualizar);
    return () => consulta.removeEventListener('change', atualizar);
  }, []);
  return compacto;
}

function ItemDoBreadcrumb({ segmento }: { readonly segmento: BreadcrumbSegment }): ReactElement {
  if (segmento.isCurrent) {
    return (
      <BreadcrumbItem className="topbar-breadcrumb__atual">
        <BreadcrumbPage className="truncate font-semibold" title={segmento.label}>{segmento.label}</BreadcrumbPage>
      </BreadcrumbItem>
    );
  }
  return (
    <BreadcrumbItem className="shrink-0">
      {segmento.href ? <BreadcrumbLink href={segmento.href}>{segmento.label}</BreadcrumbLink> : <span>{segmento.label}</span>}
    </BreadcrumbItem>
  );
}

export function ShellTopbar({ pathname }: ShellTopbarProps): ReactElement {
  const percurso = resolverBreadcrumbs(pathname, typeof window === 'undefined' ? '' : window.location.search);
  const { toggleSidebar, state, isMobile, openMobile } = useSidebar();
  const compacto = useBreadcrumbCompacto();
  const recolhido = isMobile ? !openMobile : state === 'collapsed';
  const rotuloMenu = recolhido ? 'Expandir menu' : 'Recolher menu';

  const raiz: BreadcrumbSegment = { label: 'Pieltec', href: '/painel', isCurrent: false };
  const itens = [raiz, ...percurso];
  const intermediarios = compacto ? itens.slice(1, -1) : [];
  const visiveis = compacto && intermediarios.length > 0 ? [itens[0], itens[itens.length - 1]] : itens;

  return (
    <header className="workspace-topbar sticky top-0 z-10 flex items-center shrink-0">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            aria-label={rotuloMenu}
            aria-keyshortcuts="Control+B"
            className="topbar-botao-menu shrink-0"
          >
            <PanelLeft className="size-4" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent sideOffset={8}>{rotuloMenu}</TooltipContent>
      </Tooltip>

      <Breadcrumb className="topbar-breadcrumb hidden min-w-0 sm:flex">
        <BreadcrumbList className="w-full min-w-0 flex-nowrap gap-2 sm:gap-2">
          {visiveis.map((segmento, i) => segmento && (
            <Fragment key={`${segmento.label}-${i}`}>
              {i > 0 && <BreadcrumbSeparator />}
              {i === 1 && intermediarios.length > 0 && (
                <>
                  <BreadcrumbItem className="shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="topbar-botao-reticencias" aria-label="Mostrar caminho completo" data-testid="breadcrumb-reticencias">
                          <MoreHorizontal className="size-4" aria-hidden="true" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="min-w-48">
                        {intermediarios.map((item) => (
                          item.href
                            ? <DropdownMenuItem key={item.label} asChild><a href={item.href}>{item.label}</a></DropdownMenuItem>
                            : <DropdownMenuItem key={item.label} disabled>{item.label}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}
              <ItemDoBreadcrumb segmento={segmento} />
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>

      <AlvoSlotTopbar />

      <div className="topbar-acoes ml-auto flex shrink-0 items-center gap-2">
        <AjudaContextual key={pathname} pathname={pathname} />
        <IndicadorAoVivo />
        <AppearanceToggle />
      </div>
    </header>
  );
}
